/**
 * Map utility functions for zoom calculations and bounds management
 */

/**
 * Calculate appropriate zoom level based on geofence radius
 * Larger radius requires more zoom out (lower zoom level)
 *
 * @param radiusMeters - Radius of the geofence in meters
 * @returns Appropriate zoom level (1-20)
 */
export function calculateZoomForRadius(radiusMeters: number): number {
  // Google Maps zoom levels: 1 (world) to 20 (buildings)
  // Add buffer to show the full circle comfortably (2.5x radius visible)
  const bufferMultiplier = 2.5;
  const visibleRadius = radiusMeters * bufferMultiplier;

  // Approximate meters per pixel at different zoom levels (at equator)
  // These values work well for most latitudes
  if (visibleRadius > 10000) {return 11;}  // > 10km
  if (visibleRadius > 5000) {return 12;}   // 5-10km
  if (visibleRadius > 2000) {return 13;}   // 2-5km
  if (visibleRadius > 1000) {return 14;}   // 1-2km
  if (visibleRadius > 500) {return 15;}    // 500m-1km
  if (visibleRadius > 200) {return 16;}    // 200-500m
  if (visibleRadius > 100) {return 17;}    // 100-200m
  return 18;                              // < 100m
}

/**
 * Calculate bounds that encompass all geofences with padding
 *
 * @param geofences - Array of geofences with coordinates and radius
 * @returns Google Maps LatLngBounds object or null if no valid geofences
 */
export function calculateBoundsForGeofences(
  geofences: Array<{
    centerLatitude: number | string;
    centerLongitude: number | string;
    radiusMeters: number;
  }>
): google.maps.LatLngBounds | null {
  if (!geofences.length) {return null;}

  const bounds = new google.maps.LatLngBounds();

  geofences.forEach((geofence) => {
    const lat = typeof geofence.centerLatitude === 'string'
      ? parseFloat(geofence.centerLatitude)
      : geofence.centerLatitude;
    const lng = typeof geofence.centerLongitude === 'string'
      ? parseFloat(geofence.centerLongitude)
      : geofence.centerLongitude;

    // Skip invalid coordinates
    if (isNaN(lat) || isNaN(lng)) {return;}

    const radius = geofence.radiusMeters;

    // Calculate approximate bounds for the circle
    // 1 degree latitude ≈ 111,000 meters
    // 1 degree longitude varies by latitude, but this approximation works well
    const latOffset = (radius / 111000);
    const lngOffset = (radius / (111000 * Math.cos(lat * Math.PI / 180)));

    // Extend bounds to include the circle
    bounds.extend(new google.maps.LatLng(lat + latOffset, lng + lngOffset));
    bounds.extend(new google.maps.LatLng(lat - latOffset, lng - lngOffset));
    bounds.extend(new google.maps.LatLng(lat + latOffset, lng - lngOffset));
    bounds.extend(new google.maps.LatLng(lat - latOffset, lng + lngOffset));
  });

  return bounds;
}

/**
 * Parse coordinate value from string or number
 *
 * @param value - Coordinate value (string or number)
 * @returns Parsed number or 0 if invalid
 */
export function parseCoordinate(value: string | number): number {
  if (typeof value === 'number') {return value;}
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
}
